// @flow
import * as React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { Link, withRouter } from 'react-router-dom';

import { getDefaultLeagueRoute } from '../../helpers/league';

import LeagueAvatar from '../Leagues/LeagueAvatar';
import LeagueName from '../Leagues/LeagueName';
import ArrowDownIcon from '../Icons/ArrowDown';

import colors from '../../assets/css/colors';
import { above } from '../../assets/css/media';

const Container = styled.div`
	position: relative;
	max-width: 320px;
	min-width: 100%;
`;

const DropdownButton = styled.button`
	border: 0 none;
	margin: 0;
	background: ${({ open }) => open ? colors.title : 'transparent'};

	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 13px;
	font-weight: bold;
	color: ${({ open }) => open ? colors.nrlGreen : '#ffffff'};
	padding: 0 28px 0 10px;
	height: 55px;
	min-height: 55px;
	min-width: 100%;
	max-width: 100%;

	${above.phone`
		padding: 0 48px 0 30px;
	`}
`;

const DropdownContainer = styled.div`
	position: absolute;
	z-index: 9;
	top: 100%;
	right: 0;

	min-width: 260px;
	width: 100%;

	display: flex;
	flex-direction: column;
	background: ${colors.title};
	color: white;
	border-bottom: 10px solid ${colors.nrlGreen};
`;

const ButtonText = styled.div`
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const IconWrapper = styled.div`
	position: absolute;
	right: 10px;
	display: flex;

	${above.phone`
		right: 30px;
	`}
`;

const LeagueListContainer = styled.div`
	overflow-y: auto;
	min-height: 80px;
	max-height: calc(100vh - 268px);
	width: 100%;
`;

const LeagueList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;

	display: flex;
    width: 100%;
    flex-direction: column;
`;

const LeagueListItem = styled(Link)`
	display: flex;
	align-items: center;
	max-width: 100%;

	padding: 10px 20px;

	text-decoration: none;
`;

const LeagueAvatarContainer = styled.div`
	width: 40px;
	height: 40px;
	margin-left: 10px;
	flex: 0 0 40px;
`;

const LeagueNameStyled = styled(LeagueName)`
	flex: 1 1 100%;
	width: 170px;
	text-align: right;
`;

const LeaguesMenu = styled.div`
	display: flex;
	flex-direction: column;

	margin: 20px 0;
`;

const LeagueMenuItems = styled(Link)`
	font-size: 12px;
	font-weight: bold;
	color: ${colors.nrlGreen};
	text-transform: uppercase;
	text-align: right;
	text-decoration: none;

	padding: 12px 20px;
`;

const themeLeagueSummary = is_selected => ({
	leagueName: {
		color: is_selected ? colors.nrlGreen : '#fff',
		children: {
			commissioner: {
				color: is_selected ? colors.nrlGreen : '#fff'
			}
		}
	}
});

type Props = {
	leagues: Array<Object>,
	selected_league: ?{
		id?: number,
		name?: string
	},
	user_id: number,
	location: {
		pathname: string,
		search: string,
		hash: string,
		key?: string,
		state?: any
	},
	match: Object,
	history: Object
}

type State = {
	open: boolean
}

class LeagueSwitcher extends React.Component<Props, State> {
	state = {
		open: false
	}

	myRef: string | React.Ref<typeof LeagueSwitcher> = React.createRef();

	componentDidMount() {
		window.document.addEventListener('mousedown', this.handleClickOutside, false);
	}

	componentWillUnmount() {
		window.document.removeEventListener('mousedown', this.handleClickOutside, false);
	}

	componentDidUpdate(prev_props: Props) {
		const { location: { pathname } } = this.props;

		if (pathname !== prev_props.location.pathname) {
			this.setState({
				open: false
			});
		}
	}

	handleClickOutside = (event: SyntheticEvent<any>) => {
		if (
			this.myRef &&
			this.myRef.current &&
			typeof this.myRef.current.contains === 'function' &&
			!this.myRef.current.contains(event.target)
		) {
			this.setState({
				open: false
			});
		}
	}

	toggleOpen = () => {
		this.setState(state => ({
			open: !state.open
		}));
	}

	renderLeagueMenu() {
		return (
			<LeaguesMenu>
				<LeagueMenuItems to="/leagues">My Leagues</LeagueMenuItems>
				<LeagueMenuItems to="/leagues/join">Join a League</LeagueMenuItems>
				<LeagueMenuItems to="/leagues/create">Create a League</LeagueMenuItems>
			</LeaguesMenu>
		);
	}

	render() {
		const { leagues, selected_league, user_id } = this.props;
		const { open } = this.state;

		const selected_label = selected_league ? selected_league.name : 'Select a league';

		return (
			<Container innerRef={this.myRef}>
				<DropdownButton open={open} onClick={this.toggleOpen}>
					<ButtonText>{selected_label}</ButtonText>
					<IconWrapper><ArrowDownIcon /></IconWrapper>
				</DropdownButton>
				{open && (
					<DropdownContainer>
						{leagues && (
							<LeagueListContainer>
								<LeagueList>
									{leagues.map(league => {
										const is_commissioner = league.commissioner === user_id;
										const leagueCommissionerProps = is_commissioner ?
											{ commissioner: "You are the Commissioner" } :
											{
												firstname: league.firstname,
												lastname: league.lastname
											};
										const is_selected =
											selected_league && league.id === selected_league.id;
										const default_league_route = getDefaultLeagueRoute(league);

										return (
											<ThemeProvider
												key={league.id}
												theme={themeLeagueSummary(is_selected)}
											>
												<LeagueListItem
													to={default_league_route || `/leagues`}
													selected={is_selected}
												>
													<LeagueNameStyled
														{...leagueCommissionerProps}
														name={league.name}
														width="100%"
													/>
													<LeagueAvatarContainer>
														<LeagueAvatar league={league} />
													</LeagueAvatarContainer>
												</LeagueListItem>
											</ThemeProvider>
										);
									})}
								</LeagueList>
							</LeagueListContainer>
						)}
						{this.renderLeagueMenu()}
					</DropdownContainer>
				)}
			</Container>
		);
	}
}

export default withRouter(LeagueSwitcher);


// WEBPACK FOOTER //
// ./src/components/MenuBar/LeagueSwitcher.js